import { ValidationItems } from '@kanda-libs/ks-component-ts';
import { useFinanceCalculatorContext } from 'components/shared/FinanceCalculator/context';
import { useMemo } from 'react';
import { formatToCurrency, groupByAprBNPL } from 'utils';

export interface AmountToBorrowHook {
  isLoading: boolean;
  validation: ValidationItems;
}

export default function useAmountToBorrow(): AmountToBorrowHook {
  const { isLoading, stored, rates } = useFinanceCalculatorContext();

  const apr = useMemo(() => stored.apr, [stored]);
  const type = useMemo(() => stored.type, [stored]);

  const grouped = useMemo(() => {
    if (isLoading || !rates) return undefined;
    return groupByAprBNPL(rates);
  }, [isLoading, rates]);

  const max = useMemo(() => {
    if (!apr || !grouped) return 0;
    const selected = grouped[apr];
    if (!selected) return 0;
    return Math.max(...selected.map((rate) => rate.max_job_value || 5000000));
  }, [apr, grouped]);

  const min = useMemo(() => {
    if (!apr || !grouped) return 0;
    const selected = grouped[apr];
    if (!selected) return 0;
    return Math.min(...selected.map((rate) => rate.min_job_value || 0));
  }, [apr, grouped]);

  const validation = useMemo(
    () => ({
      validate: {
        value: {
          typeRequired: (value?: string | number | boolean) =>
            typeof value === 'number' && type === 'monthlyPayments',
          minCustom: (value?: string | number | boolean) => {
            if (typeof value !== 'number') return false;
            if (!min) return true;
            return value >= min;
          },
          maxCustom: (value?: string | number | boolean) => {
            if (typeof value !== 'number') return false;
            if (!max) return true;
            return value <= max;
          },
        },
        message: {
          typeRequired: 'You must provide an amount to borrow',
          minCustom: `Amount to borrow must be above ${formatToCurrency(min)}`,
          maxCustom: `Amount to borrow cannot exceed ${formatToCurrency(max)}`,
        },
      },
    }),
    [min, max, type],
  );

  return {
    isLoading,
    validation,
  };
}
